
// src/app/config/angularjs-config-adapter.ts
import { ConfigService } from './config.service';

// Declare the global AngularJS symbol for TypeScript
declare const angular: {
    module: (name: string) => any;
};

/**
 * Applies runtime config from ConfigService to the AngularJS configuration module
 * by registering constants/values on 'EICDataStream3.config' before AngularJS bootstraps.
 *
 * Preferred approach: expose full-object config slices as AngularJS constants.
 * Compatibility: keep legacy value tokens used by existing AngularJS consumers
 * (`FEATURE_SWITCHES` and `MESSAGE_OF_THE_DAY`).
 *
 * Assumptions:
 *  - Angular APP_INITIALIZER has completed (ConfigService is ready).
 *  - The AngularJS config module 'EICDataStream3.config' exists (loaded scripts).
 */
export function applyAngularJsConfigFromRuntime(cfg: ConfigService): void {
    // Fail fast if the AngularJS config module cannot be resolved.
    let ng1ConfigModule: any;
    try {
        ng1ConfigModule = angular.module('EICDataStream3.config');
    } catch (e) {
        console.error('AngularJS module "EICDataStream3.config" not found. Ensure its script is loaded before bootstrap.', e);
        throw e;
    }

    // Read runtime config slices. Guarded getters throw if called before config load.
    const appMeta = cfg.get('appMeta');
    const productIdentity = cfg.productIdentity();

    const membership = cfg.get('membership');
    const auth = cfg.auth();
    const productUris = cfg.get('productUris');
    const dashboard = cfg.get('dashboard');
    const geographicalSummary = cfg.get('geographicalSummary');
    const featureConfig = cfg.features();
    const notifications = {
        useNewNotifications: !!(featureConfig.notifications?.useNewNotifications ?? featureConfig.useNewNotifications)
    };

    const features = cfg.get('features');
    const legacySwitches = features.legacySwitches;
    const messageOfTheDay = cfg.get('messageOfTheDay');

    // -----
    // Register constants/values in the AngularJS config module.
    // Registrations are queued and consumed during AngularJS bootstrap.
    // -----

    // AngularJS constants
    ng1ConfigModule
        .constant('PRODUCT_IDENTITY', productIdentity)
        .constant('APP_META', appMeta)
        .constant('AUTH_CONFIG', auth)
        .constant('MEMBERSHIP_CONFIG', membership)
        .constant('PRODUCT_URIS_CONFIG', productUris)
        .constant('DASHBOARD_CONFIG', dashboard)
        .constant('GEOGRAPHICAL_SUMMARY_CONFIG', geographicalSummary)
        .constant('NOTIFICATIONS_CONFIG', notifications);

    // AngularJS values (complex object; could also be defined as .constant)
    ng1ConfigModule.value('FEATURE_SWITCHES', legacySwitches);
    ng1ConfigModule.value('MESSAGE_OF_THE_DAY', messageOfTheDay);
}
